import { useState, useEffect } from "react";
import {
  AppProvider,
  Tabs,
  Page,
  Box,
  Card,
  BlockStack,
  Banner,
  Select,
  InlineStack,
  Button,
  Text,
} from "@shopify/polaris";
import SettingsPage from "./pages/SettingsPage";
import OnboardingHelpCard from "./components/OnboardingHelpCard";
import SubmissionsPage from "./pages/SubmissionsPage";
import HomePage from "./pages/HomePage";
import OnboardingPage from "./pages/OnboardingPage";
import AppFooter from "./components/AppFooter";
import { I18nProvider, useTranslation } from "./hooks/useTranslation";
import { NotificationProvider, useNotification } from "./hooks/useNotification";
import { useApi } from "./hooks/useApi";

interface AppContentProps {
  locale: string;
  setLocale: (locale: string) => void;
}

function AppContent({ locale, setLocale }: AppContentProps) {
  const { t } = useTranslation();
  const { notifications, dismissNotification } = useNotification();
  const { Call } = useApi({ baseUrl: "/api" });

  // Onboarding gate
  const [onboardingStatus, setOnboardingStatus] = useState<string | null>(null);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  const [showWelcomeSplash, setShowWelcomeSplash] = useState(false);
  const [skipOnboardingCheck, setSkipOnboardingCheck] = useState(false);

  // Read URL params once on mount and store in state
  const [urlProcessed, setUrlProcessed] = useState(false);
  const [shopParam, setShopParam] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  // Check onboarding status on mount (with retry for App Bridge initialization)
  useEffect(() => {
    const checkOnboarding = async (attempt = 0) => {
      // Set when this call defers to a later step (retry or an OAuth
      // install redirect already in flight) rather than reaching a final
      // status, so the loading screen stays up instead of flashing the
      // dashboard/onboarding UI right before the browser navigates away.
      let isTransient = false;
      try {
        const response = await Call("/onboarding/status", "GET");
        if (response.needs_installation) {
          isTransient = true;
          return;
        }
        if (response.success && response.data) {
          setOnboardingStatus(response.data.onboarding_status);
          if (response.data.show_welcome) {
            setShowWelcomeSplash(true);
          }
        } else if (response.error === "Authentication required" && attempt < 2) {
          // App Bridge not ready yet — retry after delay
          isTransient = true;
          setTimeout(() => checkOnboarding(attempt + 1), 1500);
          return;
        } else {
          setOnboardingStatus("pending_form");
        }
      } catch (e) {
        // Network error — don't block the app
        setOnboardingStatus("completed");
      } finally {
        if (!isTransient) {
          setOnboardingLoaded(true);
        }
      }
    };
    checkOnboarding();
  }, []);

  // Process URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const installed = params.get("installed");
    const subscription = params.get("subscription");
    const shop = params.get("shop");
    const tab = params.get("tab");

    console.log("[App] Current URL params:", {
      installed,
      subscription,
      shop,
      locale: params.get("locale"),
      tab,
    });

    setShopParam(shop);

    if (tab) {
      setSelectedTab(parseInt(tab, 10));
    }

    if (installed === "true") {
      sessionStorage.setItem("just_installed", "true");
    }

    if (subscription) {
      sessionStorage.setItem("subscription_status", subscription);
    }

    setUrlProcessed(true);
  }, []);

  const tabs = [
    {
      id: "dashboard",
      content: t("app.tabs.dashboard"),
      panelID: "dashboard",
    },
    {
      id: "submissions",
      content: t("app.tabs.submissions"),
      panelID: "submissions-panel",
    },
    {
      id: "settings",
      content: t("app.tabs.configuration"),
      panelID: "settings-panel",
    },
  ];

  // Loading state — neutral branded card; no spinner
  if (!urlProcessed || !onboardingLoaded) {
    return (
      <Page fullWidth>
        <Card>
          <Box padding="800">
            <BlockStack gap="300" inlineAlign="center">
              <Text as="h2" variant="headingMd" alignment="center">
                {t("app.loading.title")}
              </Text>
              <Text as="p" tone="subdued" alignment="center">
                {t("app.loading.subtitle")}
              </Text>
            </BlockStack>
          </Box>
        </Card>
      </Page>
    );
  }

  // First-install welcome splash — shown once; user clicks through
  if (showWelcomeSplash) {
    const handleWelcomeStart = () => {
      Call("/onboarding/welcome-shown", "POST").catch(() => {});
      setShowWelcomeSplash(false);
      setSkipOnboardingCheck(true);
    };
    return (
      <Page>
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="500">
              <BlockStack gap="200">
                <Text as="h2" variant="headingXl">
                  {t("onboarding.welcome.title")}
                </Text>
                <Text as="p" variant="bodyLg" tone="subdued">
                  {t("onboarding.welcome.subtitle")}
                </Text>
              </BlockStack>
              <Banner tone="info">
                <p>
                  {t("onboarding.welcome.durationText", {
                    duration: t("onboarding.welcome.durationBold"),
                  })}
                </p>
              </Banner>
              <InlineStack align="end">
                <Button variant="primary" size="large" onClick={handleWelcomeStart}>
                  {t("onboarding.welcome.startButton")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
          <OnboardingHelpCard />
        </BlockStack>
      </Page>
    );
  }

  // Show onboarding wizard if not completed
  const needsOnboarding =
    onboardingStatus === "pending_form" ||
    onboardingStatus === "pending_signing";

  if (needsOnboarding) {
    return (
      <OnboardingPage
        onComplete={() => setOnboardingStatus("completed")}
        skipInitialCheck={skipOnboardingCheck}
      />
    );
  }

  return (
    <Page fullWidth>
      {/* Global Notifications */}
      {notifications.length > 0 && (
        <Box paddingBlockEnd="400">
          <BlockStack gap="200">
            {notifications.map((notification) => (
              <Banner
                key={notification.id}
                title={notification.title}
                tone={notification.tone}
                onDismiss={() => dismissNotification(notification.id)}
              >
                {notification.message}
              </Banner>
            ))}
          </BlockStack>
        </Box>
      )}

      {/* Tabs */}
      <Box paddingBlockEnd="400">
        <Card padding="0">
          <Box paddingInline="400" paddingBlock="200">
            <InlineStack align="space-between" blockAlign="center">
              <div style={{ flex: 1 }}>
                <Tabs
                  tabs={tabs}
                  selected={selectedTab}
                  onSelect={setSelectedTab}
                />
              </div>
              <div style={{ width: "110px" }}>
                <Select
                  label=""
                  labelHidden
                  options={[
                    { label: "English", value: "en" },
                    { label: "Ελληνικά", value: "el" },
                  ]}
                  value={locale}
                  onChange={setLocale}
                />
              </div>
            </InlineStack>
          </Box>
        </Card>
      </Box>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <HomePage shop={shopParam} tabFn={setSelectedTab} />
      )}
      {selectedTab === 1 && <SubmissionsPage shop={shopParam} />}
      {selectedTab === 2 && <SettingsPage shop={shopParam} />}

      {/* App Footer */}
      <Box paddingBlockStart="800">
        <AppFooter />
      </Box>
    </Page>
  );
}

export default function App() {
  const urlParams = new URLSearchParams(window.location.search);
  const [locale, setLocale] = useState(urlParams.get("locale") || "el");

  return (
    <I18nProvider locale={locale}>
      <NotificationProvider>
        <AppProvider i18n={{}}>
          <AppContent locale={locale} setLocale={setLocale} />
        </AppProvider>
      </NotificationProvider>
    </I18nProvider>
  );
}
